import { DatePipe } from '@angular/common';
import { Component, computed, effect, forwardRef, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import {
	AbstractControl,
	ControlValueAccessor,
	FormBuilder,
	NG_VALIDATORS,
	NG_VALUE_ACCESSOR,
	ReactiveFormsModule,
	ValidationErrors,
	Validators,
} from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslocoPipe } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { CLARIFICATION_TYPE, ClarificationValue } from '../../../api/app-types/relative.app';
import { hasError } from '../../../shared/helpers/has-error.func';
import { LvrAppendAsteriskPipe } from '../../../shared/pipes/append-asterisk.pipe';
import { ImmediateErrorStateMatcher } from '../../../shared/validators/immediate-error-state-matcher';
import { CustomDateAdapter } from '../../adapters/custom-date-adapter';
import { CUSTOM_DATE_FORMATS } from '../../adapters/custom-date-format';
import { LvrInputDirective } from '../../directives/lvr-input.directive';
import { LvrField } from '../field/field';
import { normalizeToStartOfDay } from '../../../shared/helpers/date.func';

@Component({
	selector: 'lvr-data-protection',
	imports: [
		MatFormFieldModule,
		ReactiveFormsModule,
		MatDatepickerModule,
		MatSelectModule,
		MatCheckboxModule,
		MatInputModule,
		DatePipe,
		TranslocoPipe,
		LvrInputDirective,
		LvrField,
		LvrAppendAsteriskPipe,
	],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => LvrDataProtection),
			multi: true,
		},
		{
			provide: NG_VALIDATORS,
			useExisting: forwardRef(() => LvrDataProtection),
			multi: true,
		},
		{ provide: DateAdapter, useClass: CustomDateAdapter },
		{ provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
		{ provide: MAT_DATE_LOCALE, useValue: 'de-DE' },
	],
	templateUrl: './data-protection.html',
	styleUrl: './data-protection.scss',
})
export class LvrDataProtection implements ControlValueAccessor, OnInit, OnDestroy {
	private readonly env = inject(ENVIRONMENT);
	private readonly fb = inject(FormBuilder);
	private readonly destroy$ = new Subject<void>();

	// Locks the form after this information was already given and makes it readonly
	public readonly locked = input<boolean>(false);

	public readonly label = input<string>('base.field.privacy');
	public readonly checkboxLabel = input<string>('client.relatives.create.privacy_yes');

	public readonly minDateInput = input<Date | null | undefined>(undefined);
	public readonly maxDateInput = input<Date | null | undefined>(undefined);

	public readonly showAsterisk = input<boolean>(false);
	public hasError = hasError;
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly dateFormatHint = this.env.visualDateFormat;
	public readonly dateDisplayFormat = CUSTOM_DATE_FORMATS.display.dateInput;

	private readonly defaultMaxDate = signal(new Date());
	private readonly defaultMinDate = computed(() => {
		const fallbackDays = this.env.formConfiguration.contact.dateWarningRange.max ?? 42;

		const min = new Date(this.defaultMaxDate());
		min.setDate(min.getDate() - fallbackDays);

		return normalizeToStartOfDay(min);
	});

	public readonly maxDate = computed(() => {
		return this.maxDateInput() ?? this.defaultMaxDate();
	});

	public readonly minDate = computed(() => {
		return this.minDateInput() ?? this.defaultMinDate();
	});

	public readonly form = this.fb.group({
		hasBeenClarified: this.fb.nonNullable.control(false),
		clarifiedAt: this.fb.control<Date | null>({ value: null, disabled: true }),
		typeOfClarification: this.fb.control<CLARIFICATION_TYPE | null>({ value: null, disabled: true }),
	});

	// ControlValueAccessor callbacks
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private _onChange: (value: ClarificationValue | undefined) => void = () => {};
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private _onTouched: () => void = () => {};
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private _onValidatorChange: () => void = () => {};

	public ngOnInit() {
		this.handleFormValueChanges();
	}

	public ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	public writeValue(value: ClarificationValue | undefined): void {
		const has = value?.hasBeenClarified ?? false;

		this.form.patchValue(
			{
				hasBeenClarified: has,
				clarifiedAt: value?.clarifiedAt ?? null,
				typeOfClarification: value?.typeOfClarification ?? null,
			},
			{ emitEvent: false },
		);

		// Initial state
		const date = this.form.controls.clarifiedAt;
		const type = this.form.controls.typeOfClarification;

		if (has) {
			date.enable({ emitEvent: false });
			type.enable({ emitEvent: false });
			date.setValidators([Validators.required]);
			type.setValidators([Validators.required]);
		} else {
			date.disable({ emitEvent: false });
			type.disable({ emitEvent: false });
			date.clearValidators();
			type.clearValidators();
		}
		date.updateValueAndValidity({ emitEvent: false });
		type.updateValueAndValidity({ emitEvent: false });
		this._onValidatorChange();
	}

	private propagateChange(): void {
		const raw = this.form.getRawValue();
		const out: ClarificationValue = {
			hasBeenClarified: raw.hasBeenClarified ?? false,
			clarifiedAt: raw.clarifiedAt ?? undefined,
			typeOfClarification: raw.typeOfClarification ?? undefined,
		};
		this._onChange(out);
		this._onTouched();
	}

	public registerOnChange(fn: (value: ClarificationValue | undefined) => void): void {
		this._onChange = fn;
	}

	public registerOnTouched(fn: () => void): void {
		this._onTouched = fn;
	}

	public registerOnValidatorChange(fn: () => void): void {
		this._onValidatorChange = fn;
	}

	public constructor() {
		effect(() => {
			this.showAsterisk();
			this.locked();

			queueMicrotask(() => {
				this._onValidatorChange();
			});
		});
	}

	public readonly clarificationTypes = [
		{ value: CLARIFICATION_TYPE.PHONE, label: 'telefonisch' },
		{ value: CLARIFICATION_TYPE.IN_PERSON, label: 'persönlich' },
		{ value: CLARIFICATION_TYPE.MAIL, label: 'schriftlich / email' },
	];

	public getClarificationLabel(id: CLARIFICATION_TYPE | null) {
		const type = this.clarificationTypes.find((t) => t.value === id);
		return type ? type.label : '';
	}

	// Propagates form validation state
	public validate(_: AbstractControl): ValidationErrors | null {
		const has = this.form.controls.hasBeenClarified.value;
		const date = this.form.controls.clarifiedAt;
		const type = this.form.controls.typeOfClarification;
		const isRequired = this.showAsterisk();

		if (!has) {
			return isRequired ? { clarificationRequired: true } : null;
		}

		const dateInvalid =
			!date.value ||
			date.hasError('required') ||
			date.hasError('matDatepickerMin') ||
			date.hasError('matDatepickerMax') ||
			date.hasError('matDatepickerParse');

		const typeInvalid = type.value == undefined || type.hasError('required');

		if (dateInvalid || typeInvalid) {
			return {
				clarificationInvalid: {
					dateRequiredOrOutOfRange: dateInvalid,
					typeRequired: typeInvalid,
				},
			};
		}
		return null;
	}

	private handleFormValueChanges() {
		this.form.controls.hasBeenClarified.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((has) => {
			const date = this.form.controls.clarifiedAt;
			const type = this.form.controls.typeOfClarification;

			if (has) {
				date.enable({ emitEvent: false });
				type.enable({ emitEvent: false });

				// Make input fields required if the data privacy checkbox is checked
				date.setValidators([Validators.required]);
				type.setValidators([Validators.required]);
			} else {
				// Reset input fields
				date.reset({ value: null, disabled: true }, { emitEvent: false });
				type.reset({ value: null, disabled: true }, { emitEvent: false });

				date.clearValidators();
				type.clearValidators();
			}

			date.updateValueAndValidity({ emitEvent: false });
			type.updateValueAndValidity({ emitEvent: false });

			this.propagateChange();
			this._onValidatorChange();
		});

		this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.propagateChange();
			this._onValidatorChange();
		});
	}
}
